import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const TYPES = ["privacy", "terms", "cookie_or_technical_storage", "dpa"];
const empty = { type: "privacy", locale: "it", version: "", title: "", body: "", requires_acceptance: true, effective_date: "" };

export default function LegalConsole() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(empty);
  const [stats, setStats] = useState({});
  const [svc, setSvc] = useState({ service_name: "", purpose: "", provider: "", region: "", legal_status: "", dpa_reference: "", notes: "" });

  const load = useCallback(() => {
    api.get("/admin/legal/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/admin/legal/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && !user.platform_admin) return <Navigate to="/" replace />;

  const create = async () => {
    if (!form.version.trim() || !form.title.trim()) { toast.error("Versione e titolo obbligatori"); return; }
    try { await api.post("/admin/legal/documents", form); toast.success("Bozza creata"); setForm(empty); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const act = async (id, action) => {
    try { await api.post(`/admin/legal/documents/${id}/${action}`); toast.success(action === "publish" ? "Pubblicato" : "Ritirato"); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const showStats = async (id) => {
    try { const r = await api.get(`/admin/legal/documents/${id}/acceptances`); setStats((s) => ({ ...s, [id]: r.data })); } catch { /* noop */ }
  };
  const addSvc = async () => {
    if (!svc.service_name.trim()) { toast.error("Nome servizio obbligatorio"); return; }
    try { await api.post("/admin/legal/services", svc); toast.success("Servizio salvato"); setSvc({ service_name: "", purpose: "", provider: "", region: "", legal_status: "", dpa_reference: "", notes: "" }); load(); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8" data-testid="legal-console">
      <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Legal &amp; Privacy</h1>

      <section className="bg-white border border-[#E5DEC9] rounded-xl p-5 space-y-4">
        <h2 className="font-display text-lg text-[#1B3B2B]">Nuova bozza documento</h2>
        <div className="grid sm:grid-cols-4 gap-3">
          <div><Label>Tipo</Label>
            <select data-testid="legal-form-type" className="mt-1.5 w-full h-10 rounded-md border border-[#E5DEC9] bg-white px-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><Label>Locale</Label><Input className="mt-1.5" data-testid="legal-form-locale" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} /></div>
          <div><Label>Versione</Label><Input className="mt-1.5" data-testid="legal-form-version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.0" /></div>
          <div><Label>In vigore dal</Label><Input type="date" className="mt-1.5" data-testid="legal-form-effective" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
        </div>
        <div><Label>Titolo</Label><Input className="mt-1.5" data-testid="legal-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div><Label>Contenuto (bozza — NON definitivo finché non pubblicato)</Label><Textarea className="mt-1.5 min-h-[160px]" data-testid="legal-form-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
        <label className="flex items-center gap-2 text-sm text-[#2D3832]">
          <input type="checkbox" data-testid="legal-form-requires" className="h-4 w-4 accent-[#1B3B2B]" checked={form.requires_acceptance} onChange={(e) => setForm({ ...form, requires_acceptance: e.target.checked })} />
          Richiede accettazione (un typo-fix può essere pubblicato senza forzare la ri-accettazione)
        </label>
        <Button data-testid="legal-form-create" onClick={create} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Crea bozza</Button>
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-lg text-[#1B3B2B]">Documenti</h2>
        {docs.length === 0 && <p className="text-sm text-[#5F6B64]">Nessun documento.</p>}
        {docs.map((d) => (
          <div key={d.id} data-testid={`legal-doc-${d.id}`} className="bg-white border border-[#E5DEC9] rounded-xl p-4 flex flex-wrap items-center gap-3 justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#1B3B2B]">{d.title} <span className="text-xs font-normal text-[#5F6B64]">· {d.type} · {d.locale} · v{d.version} · {d.status}{d.requires_acceptance ? " · richiede accettazione" : ""}</span></p>
              {stats[d.id] && <p className="text-xs text-[#5F6B64] mt-0.5" data-testid={`legal-stats-${d.id}`}>Accettazioni: {stats[d.id].accepted}/{stats[d.id].total_users} · in attesa {stats[d.id].pending}</p>}
            </div>
            <div className="flex gap-2">
              {d.status === "draft" && <Button size="sm" data-testid={`legal-publish-${d.id}`} onClick={() => act(d.id, "publish")} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Pubblica</Button>}
              {d.status !== "retired" && <Button size="sm" variant="outline" data-testid={`legal-retire-${d.id}`} onClick={() => act(d.id, "retire")}>Ritira</Button>}
              <Button size="sm" variant="ghost" data-testid={`legal-stats-btn-${d.id}`} onClick={() => showStats(d.id)}>Accettazioni</Button>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-lg text-[#1B3B2B]">Registro servizi / responsabili</h2>
        <div className="bg-white border border-[#E5DEC9] rounded-xl p-4 grid sm:grid-cols-3 gap-2">
          {["service_name", "provider", "region", "purpose", "legal_status", "dpa_reference"].map((k) => (
            <Input key={k} data-testid={`svc-${k}`} placeholder={k} value={svc[k]} onChange={(e) => setSvc({ ...svc, [k]: e.target.value })} />
          ))}
          <Button data-testid="svc-add" onClick={addSvc} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Salva servizio</Button>
        </div>
        {services.map((s) => (
          <div key={s.id} className="text-sm text-[#2D3832] border-b border-[#F0EAE1] py-2" data-testid={`svc-row-${s.id}`}>
            <strong>{s.service_name}</strong> — {s.provider || "provider da verificare"} · {s.region || "regione da verificare"} · {s.legal_status || "stato legale da verificare"}
          </div>
        ))}
      </section>
    </div>
  );
}
